import { type RefObject, useEffect, useRef, useState } from 'react'

const canStartPan = (event: PointerEvent): boolean =>
  event.button === 1 || (event.button === 0 && event.altKey)

const findHorizontalScrollContainer = (
  from: HTMLElement
): HTMLElement | null => {
  let node: HTMLElement | null = from
  while (node) {
    const { overflowX } = getComputedStyle(node)
    if (
      (overflowX === 'auto' || overflowX === 'scroll') &&
      node.scrollWidth > node.clientWidth
    ) {
      return node
    }
    node = node.parentElement
  }
  return null
}

export const useDragToScroll = (
  ref: RefObject<HTMLElement | null>
): { isPanning: boolean } => {
  const [isPanning, setIsPanning] = useState(false)
  const panRef = useRef<{
    scrollTarget: HTMLElement
    startX: number
    startScrollLeft: number
  } | null>(null)

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const onPointerMove = (event: PointerEvent) => {
      if (!panRef.current) return
      event.preventDefault()
      const deltaX = event.clientX - panRef.current.startX
      panRef.current.scrollTarget.scrollLeft =
        panRef.current.startScrollLeft - deltaX
    }

    const endPan = () => {
      panRef.current = null
      setIsPanning(false)
      document.removeEventListener('pointermove', onPointerMove)
      document.removeEventListener('pointerup', onPointerUp)
      document.removeEventListener('pointercancel', onPointerUp)
    }

    const onPointerUp = () => {
      endPan()
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!canStartPan(event)) return
      const scrollTarget = findHorizontalScrollContainer(element)
      if (!scrollTarget) return
      event.preventDefault()
      panRef.current = {
        scrollTarget,
        startX: event.clientX,
        startScrollLeft: scrollTarget.scrollLeft
      }
      setIsPanning(true)
      document.addEventListener('pointermove', onPointerMove)
      document.addEventListener('pointerup', onPointerUp)
      document.addEventListener('pointercancel', onPointerUp)
    }

    const onAuxClick = (event: MouseEvent) => {
      if (event.button === 1) {
        event.preventDefault()
      }
    }

    element.addEventListener('pointerdown', onPointerDown)
    element.addEventListener('auxclick', onAuxClick)

    return () => {
      element.removeEventListener('pointerdown', onPointerDown)
      element.removeEventListener('auxclick', onAuxClick)
      endPan()
    }
  }, [ref])

  return { isPanning }
}
